import { cn } from '@src/lib/utils';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Label } from '@radix-ui/react-label';
import { cx } from 'class-variance-authority';
import { Check } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

interface Props extends Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    'defaultValue' | 'onChange' | 'value' | 'checked'
> {
    subTitle?: string;
    onChange?: (checked: boolean) => void;
    value?: boolean;
    defaultValue?: boolean;
    checked?: boolean;
    readOnly?: boolean;
}

const CheckboxSingleFlow = React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    Props
>(
    (
        {
            className,
            onChange,
            value,
            defaultValue,
            subTitle,
            readOnly,
            ...props
        },
        ref,
    ) => {
        const { t } = useTranslation();

        if (readOnly) {
            return (
                <div className={cn('flex flex-col gap-1')}>
                    <span className='text-xs font-normal text-(--color-grey-text)'>
                        {subTitle}
                    </span>
                    <div className='font-inter flex flex-col gap-1 text-sm font-semibold text-(--color-simple)'>
                        {value ? t('translation:yes') : t('translation:no')}
                    </div>
                </div>
            );
        }

        return (
            <div className={cx('flex items-center gap-2', subTitle && '')}>
                <CheckboxPrimitive.Root
                    ref={ref}
                    className={cn(
                        'ring-offset-background focus-visible:ring-ring grid h-4 w-4 shrink-0 place-content-center rounded-sm border border-[#D9D9D9]! focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-[var(--color-brand-green)] data-[state=checked]:bg-[var(--color-brand-green)] data-[state=checked]:text-white dark:border-[#A3A5A7]!',
                        className,
                    )}
                    {...props}
                    checked={value}
                    defaultChecked={defaultValue}
                    id={props.id ?? 'checkbox'}
                    onCheckedChange={onChange}
                >
                    <CheckboxPrimitive.Indicator
                        className={cn('grid place-content-center text-current')}
                    >
                        <Check className='h-3 w-3' />
                    </CheckboxPrimitive.Indicator>
                </CheckboxPrimitive.Root>
                {subTitle && (
                    <Label
                        className='line-clamp-6 flex min-w-0 flex-col gap-1 text-sm font-medium text-(--color-select-text)'
                        htmlFor={props.id ?? 'checkbox'}
                    >
                        {subTitle}
                    </Label>
                )}
            </div>
        );
    },
);
CheckboxSingleFlow.displayName = CheckboxPrimitive.Root.displayName;

export { CheckboxSingleFlow };
